import type { Appointment } from "@myapp/contract";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { appointmentKeys } from "../appointment.keys";
import { scheduleKeys } from "../../../schedule/api/schedule.keys";
import { workOrderKeys } from "../../../work-order/api/work-order.keys";
import { auditLogKeys } from "../../../audit-log";
import { appointmentApi } from "../service";

/**
 * Files from the field. One round trip: the API stores them and records them on the appointment,
 * so there is no "upload, then PATCH the paths back" dance as in the office form.
 */
export function useAddAppointmentTechnicianAttachments() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      id,
      files,
      workOrderId,
    }: {
      id: number;
      files: File[];
      workOrderId?: string;
    }) => {
      const { data } = await appointmentApi.uploadTechnicianAttachments(id, files);
      return { appointment: data as Appointment, workOrderId };
    },
    onSuccess: ({ appointment }) => {
      queryClient.setQueryData(appointmentKeys.detail(appointment.id), (prev: Appointment | undefined) =>
        prev ? { ...prev, technicianAttachments: appointment.technicianAttachments } : prev,
      );
    },
    onSettled: (_data, _err, vars) => {
      queryClient.invalidateQueries({ queryKey: appointmentKeys.all });
      queryClient.invalidateQueries({ queryKey: scheduleKeys.all });
      queryClient.invalidateQueries({ queryKey: workOrderKeys.all });
      if (vars?.workOrderId) {
        queryClient.invalidateQueries({ queryKey: auditLogKeys.byWorkOrder(vars.workOrderId) });
      }
    },
  });
}
